/**
 * 资源缓存管理器
 * 使用 Cache API 缓存图片和视频资源，避免重复请求
 */

const CACHE_NAME = 'ai-tools-resource-cache-v1'
const MAX_CACHE_SIZE = 500 * 1024 * 1024 // 最大缓存 500MB
const MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000 // 缓存有效期 7 天

type CacheEntry = {
  url: string
  timestamp: number
  size: number
}

/**
 * 检查浏览器是否支持 Cache API
 */
const isCacheSupported = (): boolean => {
  return 'caches' in window && 'serviceWorker' in navigator
}

/**
 * 打开缓存
 */
const openCache = async (): Promise<Cache | null> => {
  if (!isCacheSupported()) {
    return null
  }
  try {
    return await caches.open(CACHE_NAME)
  } catch (error) {
    console.warn('打开缓存失败:', error)
    return null
  }
}

/**
 * 获取缓存的资源
 */
export const getCachedResource = async (url: string): Promise<Response | null> => {
  if (!isCacheSupported()) {
    return null
  }

  try {
    const cache = await openCache()
    if (!cache) {
      return null
    }

    const cachedResponse = await cache.match(url)
    if (cachedResponse) {
      // 检查缓存是否过期
      const cacheDate = cachedResponse.headers.get('x-cache-date')
      if (cacheDate) {
        const cacheTime = parseInt(cacheDate, 10)
        const now = Date.now()
        if (now - cacheTime > MAX_CACHE_AGE) {
          // 缓存过期，删除
          await cache.delete(url)
          return null
        }
      }
      return cachedResponse
    }
    return null
  } catch (error) {
    console.warn('获取缓存资源失败:', error)
    return null
  }
}

/**
 * 缓存资源
 */
export const cacheResource = async (url: string, response: Response): Promise<void> => {
  if (!isCacheSupported()) {
    return
  }

  try {
    const cache = await openCache()
    if (!cache) {
      return
    }

    // 创建新的 Response，添加缓存时间戳和 Content-Length
    const headers = new Headers(response.headers)
    headers.set('x-cache-date', Date.now().toString())

    // 确保 Content-Length 存在（用于优化清理流程）
    if (!headers.has('Content-Length')) {
      const blob = await response.clone().blob()
      headers.set('Content-Length', blob.size.toString())
    }

    const cachedResponse = new Response(response.body, {
      status: response.status,
      statusText: response.statusText,
      headers,
    })

    await cache.put(url, cachedResponse)

    // 每次缓存新资源时检查是否需要清理
    const stats = await getCacheStats()
    if (stats.totalSize > MAX_CACHE_SIZE * 0.9) {
      // 达到 90% 时触发清理，避免等到 100% 才清理
      await cleanupCache()
    }
  } catch (error) {
    console.warn('缓存资源失败:', error)
  }
}

/**
 * 获取并缓存资源
 */
export const fetchAndCache = async (url: string): Promise<Response> => {
  // 先检查缓存
  const cachedResponse = await getCachedResource(url)
  if (cachedResponse) {
    return cachedResponse
  }

  // 缓存中没有，从网络获取
  try {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache', // 不使用浏览器默认缓存，使用我们的缓存
    })

    // 只缓存成功的响应
    if (response.ok) {
      // 克隆响应，因为 Response 只能读取一次
      const responseClone = response.clone()
      // 异步缓存，不阻塞返回
      cacheResource(url, responseClone).catch(err => {
        console.warn('异步缓存失败:', err)
      })
    }

    return response
  } catch (error) {
    console.error('获取资源失败:', error)
    throw error
  }
}

/**
 * 获取缓存的资源 Blob（推荐使用）
 * 由调用方负责创建和释放 Blob URL，避免内存泄漏
 */
export const getCachedResourceBlob = async (url: string): Promise<Blob | null> => {
  try {
    const response = await getCachedResource(url)
    if (response) {
      return await response.blob()
    }

    // 缓存中没有，从网络获取并缓存
    const networkResponse = await fetchAndCache(url)
    if (!networkResponse.ok) {
      throw new Error(`HTTP ${networkResponse.status}`)
    }
    return await networkResponse.blob()
  } catch (error) {
    console.error('获取资源 Blob 失败:', error)
    return null
  }
}

/**
 * 清理过期缓存和超出大小限制的缓存
 * 优化：从 Headers 读取大小而不是加载整个 Blob，避免清理过程占用大量内存
 */
const cleanupCache = async (): Promise<void> => {
  if (!isCacheSupported()) {
    return
  }

  try {
    const cache = await openCache()
    if (!cache) {
      return
    }

    const keys = await cache.keys()
    const entries: CacheEntry[] = []
    let totalSize = 0

    // 收集所有缓存条目信息
    for (const request of keys) {
      const response = await cache.match(request)
      if (response) {
        const cacheDate = response.headers.get('x-cache-date')
        const timestamp = cacheDate ? parseInt(cacheDate, 10) : 0

        // 优化：优先从 Content-Length 读取大小，避免加载整个 Blob
        const contentLength = response.headers.get('Content-Length')
        let size = 0

        if (contentLength) {
          size = parseInt(contentLength, 10)
        } else {
          // 如果没有 Content-Length，才加载 Blob（这是兜底方案）
          const blob = await response.blob()
          size = blob.size
        }

        entries.push({
          url: request.url,
          timestamp,
          size,
        })
        totalSize += size
      }
    }

    // 删除过期的缓存（优先执行，减少后续处理量）
    const now = Date.now()
    const expiredEntries = entries.filter(entry => now - entry.timestamp > MAX_CACHE_AGE)
    for (const entry of expiredEntries) {
      await cache.delete(entry.url)
      totalSize -= entry.size
    }

    // 如果总大小超过限制，删除最旧的缓存
    if (totalSize > MAX_CACHE_SIZE) {
      // 按时间戳排序，删除最旧的
      const validEntries = entries.filter(entry => now - entry.timestamp <= MAX_CACHE_AGE)
      validEntries.sort((a, b) => a.timestamp - b.timestamp)

      for (const entry of validEntries) {
        await cache.delete(entry.url)
        totalSize -= entry.size
        if (totalSize <= MAX_CACHE_SIZE * 0.8) {
          // 删除到 80% 以下就停止
          break
        }
      }
    }
  } catch (error) {
    console.warn('清理缓存失败:', error)
  }
}

/**
 * 清除所有缓存
 */
export const clearAllCache = async (): Promise<void> => {
  if (!isCacheSupported()) {
    return
  }

  try {
    const deleted = await caches.delete(CACHE_NAME)
    if (deleted) {
      console.log('缓存已清除')
    }
  } catch (error) {
    console.warn('清除缓存失败:', error)
  }
}

/**
 * 获取缓存统计信息
 * 优化：优先从 Content-Length 读取大小
 */
export const getCacheStats = async (): Promise<{
  count: number
  totalSize: number
}> => {
  if (!isCacheSupported()) {
    return { count: 0, totalSize: 0 }
  }

  try {
    const cache = await openCache()
    if (!cache) {
      return { count: 0, totalSize: 0 }
    }

    const keys = await cache.keys()
    let totalSize = 0

    for (const request of keys) {
      const response = await cache.match(request)
      if (response) {
        // 优先从 Content-Length 读取大小
        const contentLength = response.headers.get('Content-Length')
        if (contentLength) {
          totalSize += parseInt(contentLength, 10)
        } else {
          const blob = await response.blob()
          totalSize += blob.size
        }
      }
    }

    return {
      count: keys.length,
      totalSize,
    }
  } catch (error) {
    console.warn('获取缓存统计失败:', error)
    return { count: 0, totalSize: 0 }
  }
}

/**
 * 初始化资源缓存
 */
export const initResourceCache = (): void => {
  if (!isCacheSupported()) {
    console.log('浏览器不支持 Cache API，资源缓存功能已禁用')
    return
  }

  // 定期清理缓存（每小时清理一次）
  setInterval(() => {
    cleanupCache().catch(err => {
      console.warn('定期清理缓存失败:', err)
    })
  }, 60 * 60 * 1000)

  // 启动时清理一次
  cleanupCache().catch(err => {
    console.warn('启动清理缓存失败:', err)
  })

  console.log('资源缓存已初始化')
}
